import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import companyService from '../../services/companyService';
import userService from '../../services/userService';

// Async thunks
export const fetchCompanyUsers = createAsyncThunk(
  'company/fetchUsers',
  async (params, { rejectWithValue, getState }) => {
    try {
      // Use scope-aware userService.getUsers() instead of company-scoped companyService.getUsers()
      // Backend automatically handles scope: system_admin → all users, company_owner → own company
      // params can include: search, userType, status, companyId (for system admin), page, limit
      const result = await userService.getUsers(params);
      return result; // { users, total, page, limit }
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const fetchCompanyUserById = createAsyncThunk(
  'company/fetchUserById',
  async (userId, { rejectWithValue }) => {
    try {
      return await companyService.getUserById(userId);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const updateCompanyUser = createAsyncThunk(
  'company/updateUser',
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await companyService.updateUser(userId, data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const deleteCompanyUser = createAsyncThunk(
  'company/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      await companyService.deleteUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const fetchCompanyStats = createAsyncThunk(
  'company/fetchStats',
  async (companyId = null, { rejectWithValue }) => {
    try {
      return await companyService.getStats(companyId);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Initial state
const initialState = {
  users: [],
  usersTotal: 0,
  usersPage: 1,
  usersLimit: 10,
  usersLoading: false,
  usersError: null,
  
  currentUser: null,
  currentUserLoading: false,
  
  stats: {
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    usersByUserType: {}
  },
  statsLoading: false
};

// Slice
const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    clearUsersError: (state) => {
      state.usersError = null;
    },
    setUsersPage: (state, action) => {
      state.usersPage = action.payload;
    },
    setUsersLimit: (state, action) => {
      state.usersLimit = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch users
    builder
      .addCase(fetchCompanyUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchCompanyUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.users;
        state.usersTotal = action.payload.total;
        state.usersPage = action.payload.page;
        state.usersLimit = action.payload.limit;
      })
      .addCase(fetchCompanyUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload?.message || 'Kullanıcılar yüklenemedi';
      });
    
    // Fetch user by ID
    builder
      .addCase(fetchCompanyUserById.pending, (state) => {
        state.currentUserLoading = true;
      })
      .addCase(fetchCompanyUserById.fulfilled, (state, action) => {
        state.currentUserLoading = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchCompanyUserById.rejected, (state) => {
        state.currentUserLoading = false;
      });
    
    // Update user
    builder
      .addCase(updateCompanyUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(u => u._id === action.payload._id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      });
    
    // Delete user
    builder
      .addCase(deleteCompanyUser.fulfilled, (state, action) => {
        state.users = state.users.filter(u => u._id !== action.payload);
        state.usersTotal -= 1;
      });
    
    // Fetch stats
    builder
      .addCase(fetchCompanyStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchCompanyStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchCompanyStats.rejected, (state) => {
        state.statsLoading = false;
      });
  }
});

export const { clearUsersError, setUsersPage, setUsersLimit } = companySlice.actions;
export default companySlice.reducer;

